/**
 * Deep-freeze utility that always descends into nested objects/arrays,
 * even if the root is already frozen.
 */
export function deepFreeze<T>(obj: T): Readonly<T> {
  return _deepFreeze(obj, new WeakSet())
}

function _deepFreeze<T>(obj: T, seen: WeakSet<object>): Readonly<T> {
  // Primitives and null stay as-is
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  // Only bail out if we've _already_ visited this exact reference
  if (seen.has(obj as object)) {
    return obj as Readonly<T>
  }
  seen.add(obj as object)

  // Recurse into every own key (string or Symbol)
  for (const key of Reflect.ownKeys(obj) as Array<string | symbol>) {
    const val = (obj as never)[key]
    if (val !== null && typeof val === 'object') {
      _deepFreeze(val, seen)
    }
  }

  // Now freeze this object (works even if it was already frozen)
  return Object.freeze(obj) as Readonly<T>
}
